import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAdminRole } from "./useAdminRole";

export interface ModerationAction {
  type: "force_mute" | "force_unmute" | "force_camera_off" | "force_camera_on" | "kick" | "ban";
  targetUserId: string;
  adminId: string;
  reason?: string;
}

export interface BoothModerationState {
  mutedByAdmin: boolean;
  cameraDisabledByAdmin: boolean;
}

export const useBoothModeration = (
  boothId: string | undefined,
  currentUserId: string | undefined,
  hostId: string | undefined,
  onMutedByAdmin: () => void,
  onUnmutedByAdmin: () => void,
  onCameraDisabledByAdmin: () => void,
  onCameraEnabledByAdmin: () => void,
  onKickedFromBooth: (reason?: string) => void,
  onBannedFromBooth?: (reason?: string) => void
) => {
  const { isAdmin, isModerator, isAdminOrModerator } = useAdminRole();
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  
  const [moderationState, setModerationState] = useState<BoothModerationState>({
    mutedByAdmin: false,
    cameraDisabledByAdmin: false,
  });

  // Track moderated users (for admin UI)
  const [mutedUsers, setMutedUsers] = useState<Set<string>>(new Set());
  const [cameraDisabledUsers, setCameraDisabledUsers] = useState<Set<string>>(new Set());
  const [bannedUsers, setBannedUsers] = useState<Set<string>>(new Set());

  // Check if current user can moderate
  const isHost = currentUserId === hostId;
  const canModerate = isAdminOrModerator || isHost;

  // Subscribe to moderation events
  useEffect(() => {
    if (!boothId || !currentUserId) return;

    const channel = supabase.channel(`booth-moderation:${boothId}`);
    channelRef.current = channel;

    channel
      .on("broadcast", { event: "moderation_action" }, (payload) => {
        const action = payload.payload as ModerationAction;
        
        // If this action targets the current user
        if (action.targetUserId === currentUserId) {
          console.log("[Moderation] Received action for current user:", action.type);
          
          switch (action.type) {
            case "force_mute":
              setModerationState(prev => ({ ...prev, mutedByAdmin: true }));
              onMutedByAdmin();
              break;
            case "force_unmute":
              setModerationState(prev => ({ ...prev, mutedByAdmin: false }));
              onUnmutedByAdmin();
              break;
            case "force_camera_off":
              setModerationState(prev => ({ ...prev, cameraDisabledByAdmin: true }));
              onCameraDisabledByAdmin();
              break;
            case "force_camera_on":
              setModerationState(prev => ({ ...prev, cameraDisabledByAdmin: false }));
              onCameraEnabledByAdmin();
              break;
            case "kick":
              onKickedFromBooth(action.reason);
              break;
            case "ban":
              onBannedFromBooth?.(action.reason);
              break;
          }
        }

        // Update tracking for admin UI
        if (action.type === "force_mute") {
          setMutedUsers(prev => new Set(prev).add(action.targetUserId));
        } else if (action.type === "force_unmute") {
          setMutedUsers(prev => {
            const newSet = new Set(prev);
            newSet.delete(action.targetUserId);
            return newSet;
          });
        } else if (action.type === "force_camera_off") {
          setCameraDisabledUsers(prev => new Set(prev).add(action.targetUserId));
        } else if (action.type === "force_camera_on") {
          setCameraDisabledUsers(prev => {
            const newSet = new Set(prev);
            newSet.delete(action.targetUserId);
            return newSet;
          });
        } else if (action.type === "ban") {
          setBannedUsers(prev => new Set(prev).add(action.targetUserId));
        }
      })
      .subscribe();

    return () => {
      channel.unsubscribe();
      channelRef.current = null;
    };
  }, [boothId, currentUserId, onMutedByAdmin, onUnmutedByAdmin, onCameraDisabledByAdmin, onCameraEnabledByAdmin, onKickedFromBooth, onBannedFromBooth]);

  // Send moderation action
  const sendModerationAction = useCallback(async (
    type: ModerationAction["type"],
    targetUserId: string,
    reason?: string
  ) => {
    if (!channelRef.current || !currentUserId || !canModerate) {
      console.warn("[Moderation] Cannot send action - not authorized or no channel");
      return false;
    }

    const action: ModerationAction = {
      type,
      targetUserId,
      adminId: currentUserId,
      reason,
    };

    console.log("[Moderation] Sending action:", action);

    await channelRef.current.send({
      type: "broadcast",
      event: "moderation_action",
      payload: action,
    });

    return true;
  }, [currentUserId, canModerate]);

  // Admin action helpers
  const muteParticipant = useCallback((targetUserId: string) => {
    return sendModerationAction("force_mute", targetUserId);
  }, [sendModerationAction]);

  const unmuteParticipant = useCallback((targetUserId: string) => {
    return sendModerationAction("force_unmute", targetUserId);
  }, [sendModerationAction]);

  const disableCamera = useCallback((targetUserId: string) => {
    return sendModerationAction("force_camera_off", targetUserId);
  }, [sendModerationAction]);

  const enableCamera = useCallback((targetUserId: string) => {
    return sendModerationAction("force_camera_on", targetUserId);
  }, [sendModerationAction]);

  const kickParticipant = useCallback((targetUserId: string, reason?: string) => {
    return sendModerationAction("kick", targetUserId, reason);
  }, [sendModerationAction]);

  const banParticipant = useCallback((targetUserId: string, reason?: string) => {
    return sendModerationAction("ban", targetUserId, reason);
  }, [sendModerationAction]);

  // Check if a user is currently moderated
  const isUserMuted = useCallback((userId: string) => {
    return mutedUsers.has(userId);
  }, [mutedUsers]);

  const isUserCameraDisabled = useCallback((userId: string) => {
    return cameraDisabledUsers.has(userId);
  }, [cameraDisabledUsers]);

  const isUserBanned = useCallback((userId: string) => {
    return bannedUsers.has(userId);
  }, [bannedUsers]);

  return {
    // Current user's moderation state
    moderationState,
    
    // Permission checks
    canModerate,
    isAdmin,
    isModerator,
    isHost,
    
    // Admin actions
    muteParticipant,
    unmuteParticipant,
    disableCamera,
    enableCamera,
    kickParticipant,
    banParticipant,
    
    // State queries
    isUserMuted,
    isUserCameraDisabled,
    isUserBanned,
    mutedUsers,
    cameraDisabledUsers,
    bannedUsers,
  };
};
